import { motion } from 'framer-motion'
import { Download, Mail, Phone, MapPin, Github, Linkedin, Globe } from 'lucide-react'
import { experiences } from '../../data/experience'
import { skills, skillCategories } from '../../data/skills'
import { projects } from '../../data/projects'

const CV = () => {
  const handleDownload = () => {
    window.print()
  }

  const groupedSkills = skillCategories.map((category) => ({
    ...category,
    skills: skills.filter((skill) => skill.category === category.id)
  }))

  return (
    <section className="section">
      <div className="container-tight">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
        >
          <div>
            <span className="section-label mb-4">Curriculum Vitae</span>
            <h2 className="text-balance mb-2">Bertrand Ogen</h2>
            <p className="text-lg text-zinc-500 dark:text-zinc-400">
              Full Stack Software Engineer & DevOps Engineer
            </p>
          </div>
          <button
            onClick={handleDownload}
            className="btn-secondary self-start print:hidden"
          >
            <Download size={15} strokeWidth={1.5} />
            Print / Save PDF
          </button>
        </motion.div>

        <div className="space-y-10">
          {/* Contact Bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="flex flex-wrap gap-4 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-canvas-dark"
          >
            <a
              href="mailto:ogenbertrand@gmail.com"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-600 transition-colors duration-200 hover:text-cyan-500 dark:text-zinc-400"
            >
              <Mail size={14} strokeWidth={1.5} />
              ogenbertrand@gmail.com
            </a>
            <a
              href="tel:+237653825160"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-600 transition-colors duration-200 hover:text-cyan-500 dark:text-zinc-400"
            >
              <Phone size={14} strokeWidth={1.5} />
              +237 653 825 160
            </a>
            <span className="inline-flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-400">
              <MapPin size={14} strokeWidth={1.5} />
              Cameroon
            </span>
            <a
              href="https://github.com/Ogenbertrand"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-600 transition-colors duration-200 hover:text-cyan-500 dark:text-zinc-400"
            >
              <Github size={14} strokeWidth={1.5} />
              github.com/Ogenbertrand
            </a>
            <a
              href="https://linkedin.com/in/bertrand-ogen-2a9108264"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-600 transition-colors duration-200 hover:text-cyan-500 dark:text-zinc-400"
            >
              <Linkedin size={14} strokeWidth={1.5} />
              linkedin.com/in/bertrand-ogen
            </a>
          </motion.div>

          {/* Professional Summary */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
              Professional Summary
            </h3>
            <p className="leading-relaxed text-zinc-500 dark:text-zinc-400">
              Results-driven Full Stack Software Engineer and DevOps practitioner with over 5 years of experience designing,
              building, and deploying scalable enterprise applications. Proven open-source contributor to Keycloak, with deep
              expertise in identity and access management, cloud infrastructure, and modern Java/React ecosystems. Passionate
              about clean architecture, observable systems, and continuous learning.
            </p>
          </motion.div>

          {/* Experience */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <h3 className="mb-5 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
              Experience
            </h3>
            <div className="space-y-6">
              {experiences.map((experience) => (
                <div
                  key={experience.id}
                  className="border-b border-zinc-100 pb-6 last:border-0 last:pb-0 dark:border-zinc-800"
                >
                  <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="font-semibold text-zinc-950 dark:text-zinc-50">
                        {experience.title}
                      </h4>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {experience.company} · {experience.location}
                      </p>
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wide text-zinc-400">
                      {experience.duration}
                    </span>
                  </div>
                  <ul className="mb-3 space-y-1.5">
                    {experience.description.map((desc, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400"
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-zinc-400" />
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    {experience.technologies.map((tech) => (
                      <span key={tech} className="tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Skills */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <h3 className="mb-5 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
              Technical Skills
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {groupedSkills.map((group) => (
                <div key={group.id}>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    {group.name}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {group.skills.map((skill) => (
                      <span key={skill.name} className="tag">
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Projects */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <h3 className="mb-5 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
              Featured Projects
            </h3>
            <div className="space-y-5">
              {projects.filter((p) => p.featured).map((project) => (
                <div key={project.id}>
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="font-semibold text-zinc-950 dark:text-zinc-50">
                      {project.title}
                    </h4>
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-cyan-500 hover:text-cyan-600 dark:hover:text-cyan-400"
                    >
                      <Github size={12} strokeWidth={1.5} />
                      Code
                    </a>
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-cyan-500 hover:text-cyan-600 dark:hover:text-cyan-400"
                      >
                        <Globe size={12} strokeWidth={1.5} />
                        Live
                      </a>
                    )}
                  </div>
                  <p className="mb-2 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech) => (
                      <span key={tech} className="tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Education & Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-8 sm:grid-cols-2"
          >
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
                Education
              </h3>
              <div>
                <h4 className="font-semibold text-zinc-950 dark:text-zinc-50">
                  B.Sc. Computer Science
                </h4>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">University of Buea</p>
                <p className="font-mono text-xs text-zinc-400">2017 — 2021</p>
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-zinc-950 dark:text-zinc-50">
                Key Achievements
              </h3>
              <ul className="space-y-1.5">
                <li className="flex items-start gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-500" />
                  Keycloak open-source contributor (v26.1.0)
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-500" />
                  50+ projects delivered across web, backend, and DevOps
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-500" />
                  Enterprise experience with Java Spring Boot and React
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default CV
